import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MatchSignal } from '@/types/recommendation';
import { cn } from '@/lib/utils';

const SIGNAL_META: Record<MatchSignal['key'], { emoji: string; label: string }> = {
  utilisation:  { emoji: '⚡', label: 'Spare capacity' },
  role:         { emoji: '🎯', label: 'Role Match'    },
  skills:       { emoji: '🛠', label: 'Skills Match'  },
};

interface CriteriaBreakdownTooltipProps {
  signals:        MatchSignal[];
  softSignalsMet: number;
  /**
   * Criteria the score is out of. Defaults to the number of signals shown — the
   * skills signal is absent when the request specifies no skills.
   */
  totalCriteria?: number;
  side?:          'top' | 'right' | 'bottom' | 'left';
  children:       React.ReactNode;
}

export function CriteriaBreakdownTooltip({ signals, softSignalsMet, totalCriteria, side = 'right', children }: CriteriaBreakdownTooltipProps) {
  const total  = totalCriteria ?? (signals.length > 0 ? signals.length : 3);
  const accent = softSignalsMet >= total ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
               : softSignalsMet >= total - 1 ? 'text-amber-700 bg-amber-50 border-amber-300'
               : 'text-destructive bg-destructive/5 border-destructive/30';

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild onClick={e => e.stopPropagation()}>
          {children as React.ReactElement}
        </TooltipTrigger>
        <TooltipPrimitive.Portal>
        <TooltipContent
          side={side}
          align="center"
          sideOffset={16}
          avoidCollisions={false}
          className="p-0 w-72 shadow-2xl border border-border/80 rounded-xl overflow-hidden bg-popover !z-[9999]"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-muted/30">
            <div>
              <p className="text-xs font-semibold text-foreground">Criteria Breakdown</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{total} soft signals</p>
            </div>
            <span className={cn('text-sm font-bold px-2.5 py-1 rounded-lg border tabular-nums', accent)}>
              {softSignalsMet}/{total}
            </span>
          </div>
          <div className="px-4 py-3 space-y-3">
            {signals.map(sig => {
              const meta = SIGNAL_META[sig.key];
              return (
                <div key={sig.key} className="flex items-start gap-2">
                  <span className="text-sm leading-none mt-0.5">{meta?.emoji ?? '•'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground">{meta?.label ?? sig.label}</span>
                      <span className={cn('text-xs font-bold', sig.met ? 'text-emerald-600' : 'text-destructive')}>
                        {sig.met ? 'Met' : 'Not met'}
                      </span>
                    </div>
                    {sig.explanation && (
                      <p className="text-[10px] text-muted-foreground leading-relaxed mt-0.5">{sig.explanation}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  );
}
